import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [tenantId, setTenantId] = useState('');
  const [email, setEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get data from URL params (passed by backend after OAuth)
    const tid = searchParams.get('tenantId');
    const userEmail = searchParams.get('email');
    const userName = searchParams.get('name');
    const userDomain = searchParams.get('domain');

    if (!tid || !userEmail) {
      navigate('/login');
      return;
    }

    setTenantId(tid);
    setEmail(userEmail);
    setDomain(userDomain || userEmail.split('@')[1] || '');
    
    if (userName) {
      // Try to extract company name from user's display name
      setCompanyName(userName.split('@')[0]);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          microsoftTenantId: tenantId,
          companyName,
          domain,
          country: 'DK', // Default
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Onboarding failed');
      }

      if (data.success) {
        // Redirect to login to start trial
        window.location.href = `${API_URL}/api/auth/login?redirect=/dashboard`;
      }
    } catch (err: any) {
      setError(err.message || 'Onboarding failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'white',
        padding: '50px',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: '600px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <span style={{ fontSize: '48px' }}>🎉</span>
          <h1 style={{
            margin: '10px 0 5px',
            fontSize: '28px',
            color: '#1f2937',
          }}>
            Welcome to VirtueSign!
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px' }}>
            Let's set up your organization
          </p>
        </div>

        {/* Trial Info */}
        <div style={{
          background: '#dbeafe',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '30px',
          borderLeft: '4px solid #2563eb',
        }}>
          <strong>✨ 14-Day Free Trial</strong>
          <p style={{ margin: '5px 0 0', fontSize: '14px', color: '#1e40af' }}>
            Unlimited users • All features • No credit card required
          </p>
        </div>

        {/* Onboarding Form */}
        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              borderLeft: '4px solid #dc2626',
            }}>
              {error}
            </div>
          )}

          {/* Company Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px',
            }}>
              Company Name *
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Corporation"
              required
              autoFocus
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
            />
            <small style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginTop: '8px' }}>
              Payment details will be collected after your free trial
            </small>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'transform 0.2s',
            }}
          >
            {loading ? 'Setting up...' : 'Start Free Trial'}
          </button>
        </form>

        {/* User Info */}
        <div style={{
          marginTop: '30px',
          padding: '15px',
          background: '#f3f4f6',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#6b7280',
        }}>
          <strong>Logged in as:</strong> {email}
        </div>
      </div>
    </div>
  );
}
